import React, { createContext, useContext } from 'react'
import {
  useFloating,
  useDismiss,
  useRole,
  useInteractions,
  useId,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal
} from '@floating-ui/react'

interface ModalCloseHandler {
  close: () => void
}

const ModalContext = createContext<ModalCloseHandler>({ close: () => {} })

interface CloseButtonProps {
  ariaLabel?: string
  children: React.ReactNode
  className?: string
  onClick?: () => void
}

const CloseButton: React.FC<CloseButtonProps> = ({ children, className = '', ariaLabel = '', onClick }) => {
  const context = useContext(ModalContext)

  const handleClick = (): void => {
    context.close()
    if (onClick !== undefined) onClick()
  }

  return (
    <button className={className} aria-label={ariaLabel} onClick={handleClick}>
      {children}
    </button>
  )
}

// The exported components that wrap BaseModal provide better naming and simpler interfaces.
interface BaseModalProps {
  /** Whether the modal should be displayed or not. */
  open: boolean
  /** A function that can change the `open` value. */
  setOpen: (isOpen: boolean) => void
  /** An array of React Elements that should be "clickified" to control the render modal. */
  modalButtons: React.ReactElement
  children?: React.ReactNode
}

/**
 * A private component to encapsulate the common modal logic
 * The exported components that wrap BaseModal provide better naming and simpler interfaces.
 */
const BaseModal: React.FC<BaseModalProps> = ({
  open,
  setOpen,
  children,
  modalButtons
}) => {
  const { refs, context } = useFloating({
    open,
    onOpenChange: setOpen
  })

  const role = useRole(context)
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' })

  const { getFloatingProps } = useInteractions([
    role,
    dismiss
  ])

  const headingId = useId()
  const descriptionId = useId()

  const close = (): void => setOpen(false)

  return (
    <ModalContext.Provider value={{ close }}>
      <FloatingPortal>
        {open && (
          <FloatingOverlay className='review-reply-modal-overlay' lockScroll>
            <FloatingFocusManager context={context}>
              <div
                className='review-reply-modal pro-rounded'
                ref={refs.setFloating}
                aria-labelledby={headingId}
                aria-describedby={descriptionId}
                {...getFloatingProps()}
              >
                <CloseButton className='modal-close-button' ariaLabel='Close Modal'>
                  <span aria-hidden='true'>&times;</span>
                </CloseButton>
                <div className='modal-content'>
                  {children}
                </div>
                <div className='modal-buttons'>
                  {modalButtons}
                </div>
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        )}
      </FloatingPortal>
    </ModalContext.Provider>
  )
}

export interface ReviewReplyAlertModalProps {
  open: boolean
  setOpen: (arg0: boolean) => void
  children: React.ReactNode
}

export const ReviewReplyAlertModal: React.FC<ReviewReplyAlertModalProps> = (props) => {
  const closeButton = <CloseButton aria-label='Close' className='pro-button'>Close</CloseButton>

  return (
    <BaseModal {...props} modalButtons={closeButton} />
  )
}

export interface ReviewReplyConfirmModalProps {
  open: boolean
  setOpen: (arg0: boolean) => void
  children: React.ReactNode
  confirmationCallback: (arg0: boolean) => void
}

export const ReviewReplyConfirmModal: React.FC<ReviewReplyConfirmModalProps> = (props) => {
  const cancelButton = (
    <CloseButton
      ariaLabel='Cancel'
      className='pro-button'
      onClick={() => props.confirmationCallback(false)}
    >
      No
    </CloseButton>
  )
  const confirmButton = (
    <CloseButton
      ariaLabel='Confirm'
      className='pro-button'
      onClick={() => props.confirmationCallback(true)}
    >
      Yes
    </CloseButton>
  )

  const modalButtons = (
    <>
      {cancelButton}
      {confirmButton}
    </>
  )

  return (
    <BaseModal {...props} modalButtons={modalButtons} />
  )
}
